'use client'

import { ThreadDropdownMenu } from '@/components/Sidebar/ThreadDropdownMenu'
import { EditableTitle } from '@/components/ui/EditableTitle'
import { cn } from '@/lib/utils'
import { MoreVertical } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { FaBookmark } from 'react-icons/fa'
import { FiBookmark } from 'react-icons/fi'

type ChatHeaderProps = {
  threadName?: string
  isBookmarked?: boolean
  onRename?: (newName: string) => void
  onToggleBookmark?: () => void
  onDelete?: () => void
  scrollContainerRef?: React.RefObject<HTMLElement | null>
}

export default function ChatHeader({
  threadName = 'New Chat',
  isBookmarked = false,
  onRename,
  onToggleBookmark,
  onDelete,
  scrollContainerRef,
}: ChatHeaderProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isVisible, setIsVisible] = useState(true)
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 })
  const [triggerEdit, setTriggerEdit] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const lastScrollY = useRef(0)

  // Handle scroll to hide/show header
  useEffect(() => {
    const scrollContainer = scrollContainerRef?.current
    if (!scrollContainer) return

    const handleScroll = () => {
      const currentScrollY = scrollContainer.scrollTop

      // Don't hide if near the top (accounting for padding)
      if (currentScrollY <= 100) {
        setIsVisible(true)
        lastScrollY.current = currentScrollY
        return
      }

      // Show when scrolling up, hide when scrolling down
      if (currentScrollY < lastScrollY.current) {
        setIsVisible(true)
      } else if (currentScrollY > lastScrollY.current) {
        setIsVisible(false)
      }

      lastScrollY.current = currentScrollY
    }

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true })
    return () => scrollContainer.removeEventListener('scroll', handleScroll)
  }, [scrollContainerRef])

  // Update menu position when it opens
  useEffect(() => {
    if (isDropdownOpen && menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect()
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.left,
      })
    }
  }, [isDropdownOpen])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen])

  const handleDelete = () => {
    setIsDropdownOpen(false)
    onDelete?.()
  }

  const handleToggleBookmark = () => {
    setIsDropdownOpen(false)
    onToggleBookmark?.()
  }

  const handleRename = () => {
    setIsDropdownOpen(false)
    setTriggerEdit(true)
  }

  const handleSaveRename = async (newName: string) => {
    if (onRename) {
      await onRename(newName)
    }
  }

  return (
    <div
      className={cn(
        'group absolute left-4 z-10 transition-all duration-300',
        isVisible ? 'top-4 opacity-100' : '-top-16 opacity-0'
      )}
      ref={dropdownRef}
    >
      <div
        className={cn(
          'flex items-center rounded-full border border-gray-200/50 bg-white/70 px-4 py-2 text-sm backdrop-blur-[8px] transition-all',
          'hover:bg-white/90',
          isDropdownOpen && 'bg-white/90'
        )}
      >
        <EditableTitle
          title={threadName}
          onSave={handleSaveRename}
          className="min-w-0 max-w-[300px]"
          inputClassName="rounded-full border border-[#E0E0E0] bg-[#F3F3F3] px-4 py-2 text-sm text-left focus:border-[#B0B0B0] focus:outline-none"
          editButtonClassName="opacity-0 group-hover:opacity-100 flex-shrink-0"
          triggerEdit={triggerEdit}
          onEditTriggered={() => setTriggerEdit(false)}
        />
        <div className="ml-2 flex items-center gap-2 transition-all">
          <button
            onClick={(e) => {
              e.stopPropagation()
              onToggleBookmark?.()
            }}
            className={cn(
              'flex h-6 shrink-0 items-center justify-center rounded transition-all hover:bg-gray-100',
              isBookmarked
                ? 'w-6 opacity-100'
                : 'w-0 opacity-0 group-hover:w-6 group-hover:opacity-100'
            )}
            aria-label={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
          >
            {isBookmarked ? (
              <FaBookmark className="h-4 w-4 text-blue-600 transition-colors" />
            ) : (
              <FiBookmark className="h-4 w-4 text-gray-600 transition-colors hover:text-blue-600" />
            )}
          </button>
          <button
            ref={menuButtonRef}
            onClick={(e) => {
              e.stopPropagation()
              setIsDropdownOpen(!isDropdownOpen)
            }}
            className={cn(
              'flex h-6 shrink-0 items-center justify-center rounded transition-all hover:bg-gray-100',
              isDropdownOpen
                ? 'w-6 opacity-100'
                : 'w-0 opacity-0 group-hover:w-6 group-hover:opacity-100'
            )}
            aria-label="Thread options"
            aria-expanded={isDropdownOpen}
          >
            <MoreVertical className="h-4 w-4 text-gray-600" />
          </button>
        </div>
      </div>

      <ThreadDropdownMenu
        isOpen={isDropdownOpen}
        position={menuPosition}
        isBookmarked={isBookmarked ?? false}
        onToggleFavorite={handleToggleBookmark}
        onRename={handleRename}
        onDelete={handleDelete}
        menuRef={menuRef}
      />
    </div>
  )
}
